import { useEffect, useState } from 'react';

// Light, dark, or follow the device. Saved per device; index.html applies it before the first paint.
const STORAGE_KEY = 'mt-theme';
const THEME_CHANGED_EVENT = 'mt-theme-changed';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export const getThemePreference = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
};

export const setThemePreference = (value) => {
  try {
    if (value === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage blocked: the choice lasts until the page reloads
  }
  window.dispatchEvent(new CustomEvent(THEME_CHANGED_EVENT, { detail: value }));
};

const systemPrefersDark = () => !!window.matchMedia?.(DARK_QUERY).matches;

export const resolveTheme = (preference = getThemePreference()) =>
  preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference;

/**
 * Keeps <html data-theme> in step with the choice, on every page (public ones too): the saved
 * choice from the app, or the device setting live when there's none.
 */
export const useDocumentTheme = () => {
  useEffect(() => {
    const apply = (event) => {
      const theme = resolveTheme(event?.detail ?? getThemePreference());
      document.documentElement.dataset.theme = theme;
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0b0c0f' : '#ffffff');
    };
    apply();
    const media = window.matchMedia?.(DARK_QUERY);
    media?.addEventListener?.('change', apply);
    window.addEventListener(THEME_CHANGED_EVENT, apply);
    return () => {
      media?.removeEventListener?.('change', apply);
      window.removeEventListener(THEME_CHANGED_EVENT, apply);
    };
  }, []);
};

export const useThemePreference = () => {
  const [preference, setPreference] = useState(getThemePreference);
  useEffect(() => {
    const sync = (event) => setPreference(event.detail ?? getThemePreference());
    window.addEventListener(THEME_CHANGED_EVENT, sync);
    return () => window.removeEventListener(THEME_CHANGED_EVENT, sync);
  }, []);
  return [preference, setThemePreference];
};

// The theme on screen right now ('light' | 'dark'), following the choice and the device setting
export const useResolvedTheme = () => {
  const [theme, setTheme] = useState(() => resolveTheme());
  useEffect(() => {
    const sync = (event) => setTheme(resolveTheme(event?.detail ?? getThemePreference()));
    const media = window.matchMedia?.(DARK_QUERY);
    media?.addEventListener?.('change', sync);
    window.addEventListener(THEME_CHANGED_EVENT, sync);
    return () => {
      media?.removeEventListener?.('change', sync);
      window.removeEventListener(THEME_CHANGED_EVENT, sync);
    };
  }, []);
  return theme;
};
